import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { gqlClient } from '@/lib/graphql'

export interface BookDocs {
  id: string
  title: string
  glossaryMd: string | null
  rulesMd: string | null
  styleMd: string | null
  narrationStyle: string | null
  pronunciationMd: string | null
}

const BOOK_DOCS = gql`
  query BookDocs($slug: String!) {
    book(slug: $slug) {
      id
      title
      glossaryMd
      rulesMd
      styleMd
      narrationStyle
      pronunciationMd
    }
  }
`

const UPDATE = gql`
  mutation UpdateBookDocs(
    $bookId: ID!
    $glossaryMd: String
    $rulesMd: String
    $styleMd: String
    $narrationStyle: String
    $pronunciationMd: String
  ) {
    updateBookDocs(
      input: {
        bookId: $bookId
        glossaryMd: $glossaryMd
        rulesMd: $rulesMd
        styleMd: $styleMd
        narrationStyle: $narrationStyle
        pronunciationMd: $pronunciationMd
      }
    ) {
      book { id glossaryMd rulesMd styleMd narrationStyle pronunciationMd }
      errors
    }
  }
`

export type UpdateBookDocsVars = { bookId: string } & Partial<{
  glossaryMd: string
  rulesMd: string
  styleMd: string
  narrationStyle: string
  pronunciationMd: string
}>

export function useBookDocs(slug: string) {
  return useQuery({
    queryKey: ['book-docs', slug],
    queryFn: async () => (await gqlClient.request<{ book: BookDocs }>(BOOK_DOCS, { slug })).book,
  })
}

export function useUpdateBookDocs(slug: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: UpdateBookDocsVars) => {
      const res = await gqlClient.request<{ updateBookDocs: { errors: string[] } }>(UPDATE, vars)
      if (res.updateBookDocs.errors.length) throw new Error(res.updateBookDocs.errors.join(', '))
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['book-docs', slug] }),
  })
}
